<template>
  <section>
    <spinner v-if="isLoading"></spinner>
    <errors v-if="isVisibleErrorsModal"></errors>
    <div class="content-wrapper">
      <div class="content-heading">Payment preferences</div>
      <div class="panel panel-default">
        <dismissable-alert
          title="Your preferences have been updated. This update does not change upcoming transactions within 45 days."
          :closeAction="hidePaymentPreferenceUpdated"
          :isShowing="paymentPreferenceWasUpdated"
        />

        <div class="payment-preferences">
          <div class="row">
            <div class="col-md-12">
              <header class="separator mt-xl mb-lg">
                <p class="description">
                  Change your payment preferences by selecting between the
                  options we provide below.
                </p>
              </header>
              <div class="preferences-container">
                <div class="principal-preferences preferences">
                  <h3 class="preference-type-title">Principal maturity</h3>
                  <div
                    v-for="preference in availablePreferences"
                    :key="preference.id"
                  >
                    <div
                      :class="{
                        'radio-item': true,
                        'radio-item-unselected':
                          preference.name !== paymentPreference,
                      }"
                      :style="{ pointerEvents: isReadOnly ? 'none' : null, opacity: isReadOnly ? 0.6 : 1 }"
                      @click="setPrincipalPreference(preference.name)"
                    >
                      <input type="radio" :value="true" />
                      <span class="preference-text"></span>{{ preference.text }}
                      <label />
                    </div>
                  </div>
                </div>
                <div class="interest-preferences preferences">
                  <h3 class="preference-type-title">Interest payment</h3>
                  <div
                    v-for="preference in availablePreferences"
                    :key="preference.id"
                  >
                    <div
                      :class="{
                        'radio-item': true,
                        'radio-item-unselected':
                          preference.name !== interestPreference,
                      }"
                      :style="{ pointerEvents: isReadOnly ? 'none' : null, opacity: isReadOnly ? 0.6 : 1 }"
                      @click="setInterestPreference(preference.name)"
                    >
                      <input type="radio" :value="true" />
                      <span class="preference-text"></span>{{ preference.text }}
                      <label />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="separator"></div>
        </div>
        <button
          type="submit"
          class="btn btn-info btn-lg button-submit"
          :disabled="isReadOnly"
          @click="updatePaymentPreferences()"
        >
          Submit
        </button>
      </div>
    </div>
  </section>
</template>

<script>
import { mapState, mapActions, mapMutations } from 'vuex';

import Errors from '@/components/modals/Errors';
import Spinner from '@/components/widgets/Spinner';
import DismissableAlert from '@/components/widgets/DismissableAlert';
import {
  SET_ERRORS_MODAL_INFO,
  SHOW_ERRORS_MODAL,
  SET_ACTIVE_ACCOUNT_ID,
} from '@/store/mutation-types';
import paymentPreferenceAction from '@/data/static/upcomingTransactionActions';

export default {
  data() {
    return {
      paymentPreferenceUpdated: false,
      isLoading: false,
      paymentPreference: '',
      interestPreference: '',
      paymentPreferenceWasUpdated: false,
    };
  },
  beforeMount() {
    this.paymentPreference = this.activeAccount.paymentPreference;
    this.interestPreference = this.activeAccount.interestPreference;
  },
  methods: {
    hidePaymentPreferenceUpdated() {
      this.paymentPreferenceWasUpdated = false;
    },
    async updatePaymentPreferences() {
      if (this.isReadOnly) return;
      try {
        this.isLoading = true;
        await this.updateAccountPreferences({
          accountId: this.activeAccountId,
          payload: {
            paymentPreference: this.paymentPreference,
            interestPreference: this.interestPreference,
          },
        });
        this.setActiveAccountId({
          ...this.activeAccount,
          paymentPreference: this.paymentPreference,
          interestPreference: this.interestPreference,
        });
        this.paymentPreferenceWasUpdated = true;
      } catch (e) {
        this.setErrorsModalInfo(['Something went wrong. Please try again.']);
        this.showErrorsModal();
      } finally {
        this.isLoading = false;
      }
    },
    setPrincipalPreference(preference) {
      this.paymentPreference = preference;
    },
    setInterestPreference(preference) {
      this.interestPreference = preference;
    },
    ...mapActions('account', [
      'updateAccountPreferences',
    ]),
    ...mapMutations('errors', {
      showErrorsModal: SHOW_ERRORS_MODAL,
      setErrorsModalInfo: SET_ERRORS_MODAL_INFO,
    }),
    ...mapMutations('app', {
      setActiveAccountId: SET_ACTIVE_ACCOUNT_ID,
    }),
  },
  computed: {
    availablePreferences() {
      const preferencesArray = Object.values(paymentPreferenceAction);
      return preferencesArray.sort((a, b) => a.order - b.order);
    },
    ...mapState('errors', ['isVisibleErrorsModal']),
    ...mapState('app', ['activeAccountId', 'activeAccount', 'user']),
    isReadOnly() {
      return this.user && this.user.userType === 'READ_ONLY';
    },
  },
  components: {
    errors: Errors,
    spinner: Spinner,
    'dismissable-alert': DismissableAlert,
  },
};
</script>

<style scoped>
p {
  margin-left: 10px;
}

label {
  display: flex;
}

form.row {
  padding: 0 15px;
}

.separator {
  position: relative;
  width: 100%;
  padding-bottom: 20px;
  border-bottom: 1px dashed #eee;
}

.description {
  font-size: 16px;
}

.preference-text {
  margin-left: 20px;
}

.preference-type-title {
  font-size: 16px;
  margin-bottom: 20px;
}

.preferences-container {
  display: flex;
}

.preferences {
  width: 50%;
}

.radio-item:hover {
  cursor: pointer;
}

.button-submit {
  margin-top: 20px;
  background-color: #127285;
}

.radio-item label {
  color: #666;
  font-weight: normal;
  padding-left: 25px;
  padding-top: 5px;
  cursor: pointer;
}

.radio-item {
  height: 25px;
  margin-bottom: 20px;
}

.radio-item label:before {
  content: ' ';
  display: inline-block;
  position: absolute;
  top: 0;
  left: 0px;
  margin: 0 5px 0 0;
  width: 20px;
  height: 20px;
  border-radius: 11px;
  background-color: transparent;
}

.radio-item-unselected label:before {
  border: 2px solid #d3d3d3;
}

.radio-item label:after {
  border-radius: 5px;
  width: 10px;
  height: 10px;
  position: absolute;
  top: 5px;
  left: 5px;
  margin: auto;
  content: '';
  display: block;
  background-color: #127285;
}

.radio-item.radio-item-unselected label:after {
  display: none;
}
</style>



// WEBPACK FOOTER //
// InvestorPaymentPreferences.vue?6b6e9a90